/**
 * Floating Action Button
 *
 *
 * @example
 * new FloatingActionButton(el, {
 *   showUnderlay: true
 * });
 *
 * @module components/floating-action-button.js
 * @since 3.3.0
*/

import BaseComponent from './base';
import hasClass from '../helpers/dom/has-class';
import addClass from '../helpers/dom/add-class';
import removeClass from '../helpers/dom/remove-class';
import hasParent from '../helpers/traversal/has-parent';
import {boolean as parseBooleanAttribute, string as parseStringAttribute} from '../helpers/dom/parse-attribute';
import {get as getBreakpoint} from '../helpers/dom/breakpoint';
import mixin from '../helpers/util/mixin';
import events from '../mixins/events';
import Underlay from './underlay';

const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

class FloatingActionButton extends BaseComponent {

  /**
   * FloatingActionButton Constructor
   * @param {Element} el
   * @param {Object} params
   * @constructor
   */
  constructor(el, params = {}) {
    super(el, params);

    this._bindEventListenerCallbacks();
    this._addEventListeners();
  }


  /**
   * Create bound versions of event listener callbacks and store them. Otherwise we cannot
   * unbind from these events later because the function signatures won't match.
   * @private
   */
  _bindEventListenerCallbacks() {
    this._onClickBound = this._onClick.bind(this);
    this._onUnderlayDismissBound = this._onUnderlayDismiss.bind(this);
    this._onKeyDownBound = this._onKeyDown.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   * @private
   */
  _addEventListeners() {
    this.el.addEventListener('click', this._onClickBound);
    this.underlay.on('dismiss', this._onUnderlayDismissBound);
    this.el.addEventListener('keydown', this._onKeyDownBound, true);
  }


  /**
   * Remove event listeners for DOM events.
   * @private
   */
  _removeEventListeners() {
    this.el.removeEventListener('click', this._onClickBound);
    this.underlay.off('dismiss', this._onUnderlayDismissBound);
    this.el.removeEventListener('keydown', this._onKeyDownBound, true);
  }


  /**
   * Cache elements.
   * @param {Element} el
   * @private
   */
  _cacheElements(el) {
    this.el = el;
    this.expandEl = this.el.querySelector('.spark-fab-group__expand');
    this.collapseEl = this.el.querySelector('.spark-fab-group__collapse');

    this.underlay = new Underlay({
      interactive: true,
      display: false,
      style: 'dark'
    });
  }


  /**
   * Parse parameters from the elements.
   * @private
   */
  _parseParams() {
    this.isExtendedFab = hasClass(this.expandEl, 'spark-fab-group__expand--extended') ? true : false;
    this.showUnderlay = this.showUnderlay !== null ? this.showUnderlay : parseBooleanAttribute(this.el, 'data-show-underlay', false);
    this.underlayBreakPoint = this.underlayBreakPoint !== null ? this.underlayBreakPoint : parseStringAttribute(this.el, 'data-underlay-breakpoint', null);
    this.isActive = hasClass(this.el, 'expanded');
    this.autoClose = this.autoClose !== null ? this.autoClose : parseBooleanAttribute(this.el, 'data-auto-close', true);
  }


  /**
   * Determine whether the Underlay should be shown at a specific breakpoint.
   * @private
   */
  _determineUnderlayVisibility() {

    if (typeof(this.underlayBreakPoint) === 'number' && this.underlayBreakPoint > 0 && window.innerWidth <= this.underlayBreakPoint) {

      // The provided breakpoint is a number and meets necessary criteria for displaying Underlay
      return true;

    } else if (typeof(this.underlayBreakPoint) === 'string') {

      // The provided breakpoint is a string, so first check if it is numeric in nature
      const breakpointNumCheck = parseInt(this.underlayBreakPoint);

      if (Number.isInteger(breakpointNumCheck) && breakpointNumCheck > 0 && window.innerWidth <= breakpointNumCheck) {

        // The provided breakpoint converts to an Integer and meets necessary criteria for displaying Underlay
        return true;

      } else {

        // The provided breakpoint does not convert to an Integer so check if it is one of the preset Spark breakpoints
        const setBreakpointIndex = breakpoints.indexOf(this.underlayBreakPoint);
        const currentBreakpointIndex = breakpoints.indexOf(getBreakpoint(window.innerWidth));

        if (currentBreakpointIndex <= setBreakpointIndex && setBreakpointIndex !== -1) {
          return true;
        }
      }
    }

    // The provided breakpoint does not meet the appropriate types so do not show Underlay
    return false;
  }


  /**
   * Expand the FAB Group options and animate the width of the expand button if it is an extended FAB.
   * @public
   */
  expand() {
    // If both showUnderlay and underlayBreakPoint configs are set, determine whether
    // we should show Underlay based on current viewport size.
    if (this.showUnderlay && this.underlayBreakPoint) {
      if (this._determineUnderlayVisibility()) {
        this.underlay.show({
          interactive: true,
          style: 'dark'
        });
      }
    } else if (this.showUnderlay) {
      this.underlay.show({
        interactive: true,
        style: 'dark'
      });
    }

    addClass(this.el, 'expanded');

    const collapseBtnWidth = this.collapseEl.offsetWidth;
    const expandBtnWidth = this.expandEl.offsetWidth;

    if (this.isExtendedFab) {
      this.expandEl.setAttribute('data-width', expandBtnWidth);

      requestAnimationFrame(function() {
        this.expandEl.style.width = expandBtnWidth + 'px';

        requestAnimationFrame(function() {
          this.expandEl.style.width = collapseBtnWidth + 'px';
        }.bind(this));
      }.bind(this));
    }

    this.expandEl.setAttribute('tabindex', '-1');
    this.expandEl.setAttribute('aria-expanded', 'true');
    this.collapseEl.focus();

    this.isActive = true;

    this.trigger('expand');
    this.onExpand.call(this);
  }


  /**
   * Collapse the FAB Group options and animate the width of the expand button if it is an extended FAB.
   * @public
   */
  collapse() {
    if (this.showUnderlay) {
      if (this.underlay.active) this.underlay.hide();
    }

    removeClass(this.el, 'expanded');

    if (this.isExtendedFab) {
      this.expandEl.style.width = this.expandEl.getAttribute('data-width') + 'px';
      this.expandEl.addEventListener('transitionend', this._removeButtonAttributes());
    }

    this.expandEl.removeAttribute('tabindex');
    this.expandEl.setAttribute('aria-expanded', 'false');
    this.expandEl.focus();

    this.isActive = false;

    this.trigger('collapse');
    this.onCollapse.call(this);
  }


  /**
   * Remove attributes from the expand button and remove its event listener.
   * @private
   */
  _removeButtonAttributes() {
    this.expandEl.removeAttribute('style');
    this.expandEl.removeAttribute('data-width');
    this.expandEl.removeEventListener('transitionend', this._removeButtonAttributes);
  }


  /**
   * When the underlay is dismissed, collapse the FAB Group options.
   * @private
   */
  _onUnderlayDismiss() {
    this.collapse();
  }


  /**
   * When a control in the FAB Group is clicked, determine if we need to expand or collapse the options.
   * @param {Object} e
   * @private
   */
  _onClick(e) {
    if (!this.el.contains(e.target)) {
      return;
    }

    // If Expand or Collapse buttons are clicked
    if (e.target === this.expandEl || hasParent(e.target, this.expandEl)) {
      this.expand();
      return;
    } else if (e.target === this.collapseEl || hasParent(e.target, this.collapseEl)) {
      this.collapse();
      return;
    }

    // If related actions are clicked, determine whether FAB Group should be collapsed
    if (this.autoClose) {
      this.collapse();
    }
  }


  /**
   * When a key is pressed, determine if it is the Tab Key or Esc key.
   * On Tab, cycle through the options in the FAB Group if it is expanded.
   * On ESC, close the FAB Group if the underlay is currently active.
   * @param {Object} e
   * @private
   */
  _onKeyDown(e) {
    if (!this.showUnderlay || !this.underlay.active) {
      return;
    }

    const TAB_KEY_CODE = 9;
    const ESC_KEY_CODE = 27;

    switch (e.keyCode) {
      case ESC_KEY_CODE:
        this.collapse();
        break;
      case TAB_KEY_CODE: {
        const fabFocusableEls = this.el.querySelectorAll('a[href]:not([tabindex^="-"]):not([disabled]), button:not([tabindex^="-"]):not([disabled]), [tabindex]:not([tabindex^="-"]):not([disabled]), [tabindex="0"]');

        if (fabFocusableEls.length > 0) {
          const focusableElements = Array.prototype.slice.call(fabFocusableEls);

          if (focusableElements.length === 1) {
            e.preventDefault();
          } else if (e.shiftKey && document.activeElement === focusableElements[0]) {
            e.preventDefault();
            focusableElements[focusableElements.length - 1].focus();
          } else if (!e.shiftKey && document.activeElement === focusableElements[focusableElements.length - 1]) {
            e.preventDefault();
            focusableElements[0].focus();
          }
        }
        break;
      }
      default:
        return;
    }
  }


  /**
   * Return state of the FAB Group i.e. whether expanded(true) or collapsed(false).
   * @return {Boolean}
   * @public
   */
  get active() {
    return this.isActive;
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
FloatingActionButton.prototype._whitelistedParams = [
  'showUnderlay',
  'underlayBreakPoint',
  'autoClose',
  'onCollapse',
  'onExpand'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
FloatingActionButton.prototype.defaults = {
  el: null,
  isActive: false,
  showUnderlay: null,
  underlayBreakPoint: null,
  autoClose: null,
  onCollapse: () => {},
  onExpand: () => {}
};

mixin(FloatingActionButton.prototype, events);

export default FloatingActionButton;
